import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Stack,
  Typography,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { HeaderSection } from './HeaderSection';
import { InfoItem } from '@components/InfoItem';
import { formatCompactDate } from '../utils';
import { WorkOrderDetailStatus } from '../types';
import type { WorkOrderViewModel } from '@api/enterprise/openapi/models';

type StatusCounts = {
  [WorkOrderDetailStatus.COMPLETE]: number;
  [WorkOrderDetailStatus.INCOMPLETE]: number;
  [WorkOrderDetailStatus.SKIPPED]: number;
  [WorkOrderDetailStatus.BACKORDERED]: number;
};

type ScheduleStatusProps = {
  workOrder: WorkOrderViewModel;
  statusCounts?: StatusCounts;
};

const StatusChip = styled(Chip)(() => ({
  fontWeight: 600,
  fontSize: '0.75rem',
  height: '20px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  '& .MuiChip-label': {
    padding: '0 8px'
  }
}));

const StatusSummary = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.background.paper
      : theme.palette.grey[50],
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
  marginTop: theme.spacing(1)
}));

export const ScheduleStatus: React.FC<ScheduleStatusProps> = ({
  workOrder,
  statusCounts
}) => {
  const { t } = useTranslation('workOrders');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getStatusColors = (status: WorkOrderDetailStatus) => {
    const isDarkMode = theme.palette.mode === 'dark';

    switch (status) {
      case WorkOrderDetailStatus.COMPLETE:
        return {
          backgroundColor: isDarkMode
            ? 'rgba(76, 175, 80, 0.15)'
            : 'rgba(76, 175, 80, 0.12)',
          color: isDarkMode ? '#66bb6a' : '#4caf50',
          border: `1px solid ${isDarkMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.3)'}`
        };
      case WorkOrderDetailStatus.INCOMPLETE:
        return {
          backgroundColor: isDarkMode
            ? 'rgba(255, 152, 0, 0.15)'
            : 'rgba(255, 152, 0, 0.12)',
          color: isDarkMode ? '#ff9800' : '#f57c00',
          border: `1px solid ${isDarkMode ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.3)'}`
        };
      case WorkOrderDetailStatus.SKIPPED:
        return {
          backgroundColor: isDarkMode
            ? 'rgba(158, 158, 158, 0.15)'
            : 'rgba(158, 158, 158, 0.12)',
          color: isDarkMode ? '#9e9e9e' : '#757575',
          border: `1px solid ${isDarkMode ? 'rgba(158, 158, 158, 0.3)' : 'rgba(158, 158, 158, 0.3)'}`
        };
      case WorkOrderDetailStatus.BACKORDERED:
        return {
          backgroundColor: isDarkMode
            ? 'rgba(156, 39, 176, 0.15)'
            : 'rgba(156, 39, 176, 0.12)',
          color: isDarkMode ? '#ba68c8' : '#9c27b0',
          border: `1px solid ${isDarkMode ? 'rgba(156, 39, 176, 0.3)' : 'rgba(156, 39, 176, 0.3)'}`
        };
      default:
        return {
          backgroundColor: isDarkMode
            ? 'rgba(158, 158, 158, 0.15)'
            : 'rgba(158, 158, 158, 0.12)',
          color: isDarkMode ? '#9e9e9e' : '#757575',
          border: `1px solid ${isDarkMode ? 'rgba(158, 158, 158, 0.3)' : 'rgba(158, 158, 158, 0.3)'}`
        };
    }
  };

  const renderStatusSummary = () => {
    if (!statusCounts || isMobile) return null;

    const statusEntries = Object.entries(statusCounts).filter(
      ([_, count]) => count > 0
    );
    if (statusEntries.length === 0) return null;

    return (
      <StatusSummary>
        <Stack spacing={1}>
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            {t('workOrder.progress.summary.breakdown')}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {statusEntries.map(([status, count]) => {
              const statusKey = status.toLowerCase();
              return (
                <StatusChip
                  key={status}
                  label={t(`workOrder.progress.statusCounts.${statusKey}`, {
                    count
                  })}
                  size="small"
                  sx={getStatusColors(status as WorkOrderDetailStatus)}
                />
              );
            })}
          </Stack>
        </Stack>
      </StatusSummary>
    );
  };

  return (
    <HeaderSection
      icon={<AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
      title={t('workOrder.scheduleStatus.title')}
    >
      <InfoItem
        label={t('workOrder.scheduleStatus.openDate')}
        value={formatCompactDate(workOrder.openDate)}
      />
      <InfoItem
        label={t('workOrder.scheduleStatus.dueDate')}
        value={formatCompactDate(workOrder.expectedDueDate)}
      />
      {renderStatusSummary()}
    </HeaderSection>
  );
};
